// The voice session for one plan screen. All behaviour is in `engine.ts`; this mirrors its
// state into React and hands out stable methods.

import { useEffect, useMemo, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import {
  VoiceEngine,
  voiceSupported,
  type VoicePhase,
  type VoiceState,
  type VoiceTarget,
} from './engine.js';

export type { VoicePhase, VoiceTarget };

export type Voice = {
  /** Secure context, a microphone API and Web Audio are all present. */
  supported: boolean;
  on: boolean;
  phase: VoicePhase;
  /** What is being heard now: the listening bubble's text. */
  partial: string;
  /** The one-line reason voice ended, when it ended on its own. */
  note: string | null;
  /** Changes when speech is kept for a conversation's text box. */
  unsentVersion: number;
  /** Must be called inside the click, which unlocks audio and asks for the microphone. */
  start: () => void;
  end: () => void;
  /** Speech not yet sent for that conversation; cleared on read. */
  takeUnsent: (threadId: string) => string;
  setTarget: (target: VoiceTarget | null) => void;
  /** The microphone level, 0..1, several times a second, for the meter. */
  subscribeLevel: (listener: (level: number) => void) => () => void;
};

export function useVoice(client: ApiClient, planId: string): Voice {
  const [state, setState] = useState<VoiceState | null>(null);
  const engine = useMemo(
    () => new VoiceEngine(client, planId, setState),
    [client, planId],
  );
  useEffect(() => {
    engine.attach();
    return () => {
      engine.detach();
      engine.dispose();
    };
  }, [engine]);
  const supported = useMemo(() => voiceSupported(), []);
  // Stable for the life of the engine, so effects that depend on them do not re-run on
  // every state change.
  const methods = useMemo(
    () => ({
      start: () => engine.start(),
      end: () => engine.end(),
      takeUnsent: (threadId: string) => engine.takeUnsent(threadId),
      setTarget: (target: VoiceTarget | null) => engine.setTarget(target),
      subscribeLevel: (listener: (level: number) => void) =>
        engine.subscribeLevel(listener),
    }),
    [engine],
  );
  const current = state ?? engine.state;
  return useMemo<Voice>(
    () => ({
      supported,
      on: current.on,
      phase: current.phase,
      partial: current.partial,
      note: current.note,
      unsentVersion: current.unsentVersion,
      ...methods,
    }),
    [supported, current, methods],
  );
}
